import React, { useState, useEffect } from "react";
import axios from "api/axios";
import { Button, OutlineButton } from "components/button";
import GroupedButton from "components/button/grouped";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import { useFormik } from "formik";
import { cloneDeep } from "lodash";
import Switch from "rc-switch";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Prompt } from "react-router-dom";
import { TItemsContainer } from "types/items-container";
import { TTargetProduct } from "types/product";
import { TTargetType } from "types/target-type";
import { TTargetTax } from "types/tax";
import EditLayout from "./editLayout";
import ReadLayout from "./readLayout";
import { useTranslation } from 'react-i18next';
import { useAuthContext } from "contexts/auth";
import { LineItemProvider, useLineItemContext } from "./context/LineItemContext";

type Props = {
  id: string;
  parentId?: string;
  parentType?: "project" | "request";
  type: TTargetType;
  style?: {};
  triggerRef?: any;
  onClick?: (p: {
    products?: TTargetProduct[] | undefined;
    price_type: "sale" | "install";
  }) => void;
  onCancel?: () => void;
  view?: "read" | "edit";
  formik?: any;
  getChild?: boolean;
  /** When true, hides Edit/Save/Cancel and Edit Taxes. Omit or false = editable. */
  readOnly?: boolean;
  title?: string;
  isSent?: boolean;
  noTax: boolean;
  invalidateQueryKeys?: readonly (string | number)[][];
};

export default function LineItemsContainer(props: Props) {
  return (
    <LineItemProvider type={props.type} noTax={props.noTax}>
      <LineItemsContainerWithProvider {...props} />
    </LineItemProvider>
  );
}

function LineItemsContainerWithProvider (props: Props) {
  const { taxes, setTaxes, noTax } = useLineItemContext();

  const { t } = useTranslation();
  const tBase = "containers.lineItems.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { pricing: defaultPriceType } = useAuthContext();

  const showTaxes = !noTax;
  /** Explicit: only true disables editing; undefined/false = editable. */
  const isReadOnly = props.readOnly === true;

  const { showSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<"read" | "edit" | "edit-taxes">(() =>
    props.view === "edit" ? "edit" : "read"
  );
  const [taxAllServices, setTaxAllServices] = useState(true);
  const [taxAllParts, setTaxAllParts] = useState(true);
  const [hasServices, setHasServices] = useState(false);
  const [hasParts, setHasParts] = useState(false);
  const [initialTaxes, setInitialTaxes] = useState<TTargetTax[]>([]);

  const toggleTaxForCategory = (category: "service" | "part", bool: boolean) => {
    formik.setValues((values: any) => ({
      ...values,
      products: values.products.map((product: any) => {
        const isGrouped = product.fk_ingroup_id !== null;
        if (isGrouped) {
          // For grouped products, we need to check if the entire group is all services or all parts
          const groupId = product.fk_ingroup_id;
          const groupProducts = values.products.filter((p: any) => p.fk_ingroup_id === groupId);
          
          const isAllServices = groupProducts.every((p: any) => 
            p.type === "service" || ("fk_product_id" in p && p.fk_product_id !== null)
          );
          const isAllParts = groupProducts.every((p: any) => 
            p.type === "part" || ("fk_item_id" in p && p.fk_item_id !== null)
          );
          
          // Only apply the toggle if the group is all services or all parts
          if ((category === "service" && isAllServices) || (category === "part" && isAllParts)) {
            return { ...product, taxable: bool };
          }
          
          return product; // Leave mixed groups unchanged
        }
  
        const isTypeService = product.type === "service";
        const isTypePart = product.type === "part";
  
        if (category === "service" && isTypeService) {
          return { ...product, taxable: bool };
        }
  
        if (category === "part" && isTypePart) {
          return { ...product, taxable: bool };
        }
  
        return product;
      }),
    }));
  };
  
  useEffect(() => {
    if (props.view) setView(props.view);
  }, [props.triggerRef, props.view]);

  const API_BASE = `${process.env.REACT_APP_SERVER_URL}/api/v1/company`;

  const productsUrl =
    props.id === "new"
      ? null
      : (() => {
          const params = new URLSearchParams();
          if (props.getChild != null) params.set("get_child", String(props.getChild));
          if (props.parentType != null) params.set("parent_type", props.parentType);
          if (props.parentId != null) params.set("parent_id", props.parentId);
          const qs = params.toString();
          return `${API_BASE}/products/${props.type}/${props.id}${qs ? `?${qs}` : ""}`;
        })();

  const query = useQuery<TItemsContainer>(
    ["products", props.id, props.type],
    async () =>
      productsUrl
        ? await axios.get(productsUrl).then((res) => {
            setTaxes(res.data.taxes || []);
            return res.data;
          })
        : [],
    {
      refetchOnWindowFocus: view === "read",
    }
  );

  const mutation = useMutation(
    async (payload: {
      productsData: { products?: TTargetProduct[]; price_type?: string; hide_empty?: boolean };
      taxesToSave?: TTargetTax[];
    }) => {
      const { productsData, taxesToSave } = payload;

      // 1. Products first (establishes product_target)
      await axios.post(
        `${API_BASE}/products/${props.type}/${props.id}`,
        { ...productsData, parentId: props.parentId, parentType: props.parentType }
      );

      // 2. Taxes second (recalculateTargetTotals sees both)
      // Always call tax API when showTaxes - empty array tells server to remove all taxes
      if (showTaxes) {
        const taxPayload = [...(taxesToSave || [])]
          .sort((a, b) => (a.ordering || 0) - (b.ordering || 0))
          .map((tax) => ({
            id: tax.tax_id || tax.id,
            isGroup: tax.isGroup || false,
            type: tax.tax_type,
            ordering: tax.ordering || 0,
          }));
        await axios.post(
          `${API_BASE}/tax/${props.type}/${props.id}`,
          taxPayload
        );
      }
    }
  );

  const handleSubmit = async (e: {
    products?: TTargetProduct[] | undefined;
    price_type: "sale" | "install";
    taxes?: TTargetTax[];
  }) => {
    setLoading(true);
    const temp = cloneDeep(e);

    if (temp.products) {
      temp.products = temp.products.map((product) => ({
        ...product,
        unit_price: typeof product.unit_price === "number" ? product.unit_price * 100 : null,
        list_price: typeof product.list_price === "number" ? product.list_price * 100 : null,
        install_price: typeof product.install_price === "number" ? product.install_price * 100 : null,
      }));
    }

    const productsData = {
      products: temp.products,
      price_type: temp.price_type,
      hide_empty: (e as any).hide_empty,
    };

    try {
      await mutation.mutateAsync({
        productsData,
        taxesToSave: showTaxes ? taxes : undefined,
      });
      const message = showTaxes ? tr("Products and Taxes Updated!") : tr("Products Updated!");
      showSnackbar(message);
      queryClient.setQueryData(
        ["products", props.id, props.type],
        (old: TItemsContainer | undefined) => ({
          ...old,
          products: productsData.products,
          taxes: showTaxes ? taxes : (old?.taxes ?? []),
          price_type: productsData.price_type,
          hide_empty: productsData.hide_empty,
        })
      );
      setView("read");
      queryClient.invalidateQueries(["products", props.id, props.type]);
      props.invalidateQueryKeys?.forEach((key) => queryClient.invalidateQueries(key as [string, string]));
    } catch (error) {
      console.error("Error saving:", error);
      showSnackbar("Error saving changes");
    } finally {
      setLoading(false);
    }
  };

  const assignGroupInstanceIds = (products: any[]) => {
    if (!products || products.length === 0) return products;

    const sorted = [...products].sort((a, b) => (a.ordering || 0) - (b.ordering || 0));
    let lastGroupId: number | null = null;
    let instanceCounter = 0;
    let currentInstanceId: string | null = null;

    return sorted.map((product) => {
      if (product.fk_ingroup_id) {
        if (product.fk_ingroup_id !== lastGroupId) {
          instanceCounter++;
          lastGroupId = product.fk_ingroup_id;
          currentInstanceId = `group_${product.fk_ingroup_id}_db_${instanceCounter}`;
        }
        return { ...product, group_instance_id: currentInstanceId };
      } else {
        lastGroupId = null;
        currentInstanceId = null;
        return { ...product, group_instance_id: null };
      }
    });
  };

  const containerFormik = useFormik<{
    products?: TTargetProduct[];
    price_type: "sale" | "install";
    hide_empty: boolean;
    taxes?: TTargetTax[];
  }>({
    initialValues: {
      products: assignGroupInstanceIds(
        query.data?.products?.map((product) => ({
          ...product,
          unit_price:
            typeof product.unit_price === "number"
              ? product.unit_price / 100
              : "",
          list_price:
            typeof product.list_price === "number"
              ? product.list_price / 100
              : "",
          install_price:
            typeof product.install_price === "number"
              ? product.install_price / 100
              : "",
          taxable: product.taxable !== undefined ? product.taxable : true,
        })) || []
      ),
      price_type: query.data?.price_type || defaultPriceType || "install",
      hide_empty: query.data?.hide_empty || false,
      taxes: query.data?.taxes || [],
    },
    onSubmit: (e) => {
      if (props.onClick) {
        setView("read");
        queryClient.setQueryData(["products", props.id, props.type], e);
        props.onClick(e);
      } else {
        handleSubmit(e);
      }
    },
    enableReinitialize: view === "read",
  });

  const formik = props.formik || containerFormik;

  useEffect(() => {
    const products = formik.values.products || [];
  
    // Get all unique groups
    const groupIds = [...new Set(products.filter((p: any) => p.fk_ingroup_id).map((p: any) => p.fk_ingroup_id))];
    
    // Separate individual products and groups
    const individualServiceProducts = products.filter((p: any) => (
      (p.type === "service" || ("fk_product_id" in p && p.fk_product_id !== null))
      && ("fk_ingroup_id" in p && p.fk_ingroup_id === null) // Exclude grouped products
    ));
    
    const individualPartsProducts = products.filter((p: any) => (
      (p.type === "part" || ("fk_item_id" in p && p.fk_item_id !== null))
      && ("fk_ingroup_id" in p && p.fk_ingroup_id === null) // Exclude grouped products
    ));
    
    // Get groups that are all services or all parts
    const allServiceGroups = groupIds.filter(groupId => {
      const groupProducts = products.filter((p: any) => p.fk_ingroup_id === groupId);
      return groupProducts.every((p: any) => 
        p.type === "service" || ("fk_product_id" in p && p.fk_product_id !== null)
      );
    });
    
    const allPartsGroups = groupIds.filter(groupId => {
      const groupProducts = products.filter((p: any) => p.fk_ingroup_id === groupId);
      return groupProducts.every((p: any) => 
        p.type === "part" || ("fk_item_id" in p && p.fk_item_id !== null)
      );
    });
    
    // Check if we have any service-related items (individual or all-service groups)
    const hasServices = individualServiceProducts.length > 0 || allServiceGroups.length > 0;
    const hasParts = individualPartsProducts.length > 0 || allPartsGroups.length > 0;
    
    setHasServices(hasServices);
    setHasParts(hasParts);
  
    // Check if all service items are taxable
    const allServiceItemsTaxable = () => {
      const individualAllTaxable = individualServiceProducts.length > 0 ? 
        individualServiceProducts.every((p: any) => p.taxable === true) : true;
      
      const groupsAllTaxable = allServiceGroups.length > 0 ? 
        allServiceGroups.every(groupId => {
          const groupProducts = products.filter((p: any) => p.fk_ingroup_id === groupId);
          return groupProducts.every((p: any) => p.taxable === true);
        }) : true;
      
      return individualAllTaxable && groupsAllTaxable;
    };
    
    // Check if all parts items are taxable
    const allPartsItemsTaxable = () => {
      const individualAllTaxable = individualPartsProducts.length > 0 ? 
        individualPartsProducts.every((p: any) => p.taxable === true) : true;
      
      const groupsAllTaxable = allPartsGroups.length > 0 ? 
        allPartsGroups.every(groupId => {
          const groupProducts = products.filter((p: any) => p.fk_ingroup_id === groupId);
          return groupProducts.every((p: any) => p.taxable === true);
        }) : true;
      
      return individualAllTaxable && groupsAllTaxable;
    };
  
    setTaxAllServices(hasServices ? allServiceItemsTaxable() : false);
    setTaxAllParts(hasParts ? allPartsItemsTaxable() : false);
  }, [formik.values.products]);

  if (query.isLoading) return <Spinner />;

  const handleClick = () => {
    if (view === "read") {
      setInitialTaxes(taxes);
      setView("edit");
    } else {
      formik.handleSubmit();
    }
  };

  const handleCostChange = (type: "sale" | "install") => {
    formik.setValues((v: any) => ({ ...v, price_type: type }));
  };

  return (
    <>
      <div style={props.style} className="space-y-4">
        <div className="flex items-center justify-between border-b py-4" id="line-items-container">
          <h2>{props.title || tr("Line Items")}</h2>
          {!isReadOnly && (
            <div>
              <OutlineButton
                type="button"
                onClick={handleClick}
                isLoading={loading}
                primary
              >
                {view === "read" ? tr("Edit") : tr("Save Changes")}
              </OutlineButton>
              {view !== "read" && (
                <Button
                  onClick={() => {
                    setTaxes(initialTaxes);
                    setView("read");
                    props.onCancel?.();
                  }}
                  style={{ marginLeft: "0.5rem" }}
                >
                  {tr("Cancel")}
                </Button>
              )}
            </div>
          )}
          {props.isSent && (
            <OutlineButton
              type="button"
              onClick={() => {
                if (view === "read") {
                  setInitialTaxes(taxes);
                  setView("edit-taxes");
                } else {
                  formik.handleSubmit();
                }
              }}
              isLoading={loading}
              primary
            >
              {view === "read" ? tr("Edit Taxes") : tr("Save Changes")}
            </OutlineButton>
          )}
        </div>
        {view === "read" || view === "edit-taxes" ? (
          <ReadLayout 
            data={query.data} 
            id={props.id} 
            type={props.type}
            view={view}
          />
        ) : (
          <DynamicForm>
            <Prompt
              when={formik.dirty}
              message={tr("Changes have not been saved. Are you sure you want to leave?")}
            />
            <div className="flex justify-end items-center space-x-4 mb-4">
              <div className="space-x-2">
                <label className="text-slate-600">{tr("Hide Empty")}</label>
                <Switch
                  checked={formik.values.hide_empty}
                  onChange={() =>
                    formik.setValues((v: any) => ({
                      ...v,
                      hide_empty: !v.hide_empty,
                    }))
                  }
                />
              </div>
              {showTaxes && (
                <>
                  <div className="space-x-2">
                    <label className="text-slate-600">{tr("Tax All Services")}</label>
                    <Switch
                      disabled={!hasServices}
                      checked={taxAllServices}
                      onChange={(checked) => {
                        setTaxAllServices(checked);
                        toggleTaxForCategory("service", checked);
                      }}
                    />
                  </div>
                  <div className="space-x-2">
                    <label className="text-slate-600">{tr("Tax All Parts")}</label>
                    <Switch
                      disabled={!hasParts}
                      checked={taxAllParts}
                      onChange={(checked) => {
                        setTaxAllParts(checked);
                        toggleTaxForCategory("part", checked);
                      }}
                    />
                  </div>
                </>
              )}


              <GroupedButton className="flex">
                <Button
                  onClick={() => handleCostChange("sale")}
                  $selected={formik.values.price_type === "sale"}
                >
                  {tr("Sale")}
                </Button>
                <Button
                  onClick={() => handleCostChange("install")}
                  $selected={formik.values.price_type === "install"}
                >
                  {tr("Install")}
                </Button>
              </GroupedButton>
            </div>

            <EditLayout
              data={query.data}
              formik={formik}
              id={props.id}
              type={props.type}
            />
          </DynamicForm>
        )}
      </div>
    </>
  );
}

const DynamicForm = ({
  children,
  isForm,
}: {
  isForm?: boolean;
  children: React.ReactNode;
}) => {
  if (isForm)
    return (
      <form style={{ display: "flex", flexDirection: "column" }}>
        {children}
      </form>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
  );
};
